import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useMemo } from 'react';
import PageContent from 'layouts/PageContent';
import { getTransactionReceipt } from 'app/features/transactions/transactionDocumentSaga';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import TanstackReactTable from 'core/table/TanstackReactTable';
import { Box, Typography } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

const pdfButtonSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textTransform: 'none',
  width: 150,
  height: 150,
  borderColor: 'error.main',
};

const TransactionDocuments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { transactionId } = useParams();

  const { isLoading, data: transactionDocument } = useSelector(
    (state) => state.transactionDocument.show
  );

  useEffect(() => {
    if (!transactionId || transactionDocument?.isFetched) return;
    dispatch(getTransactionReceipt({ orderId: transactionId }));
  }, [transactionId, dispatch, transactionDocument?.isFetched]);

  const data =
    transactionDocument?.documentsList?.map((doc) => ({
      url: doc.url,
      documentName: decodeURIComponent(doc.documentName),
      extension: doc.extension?.toLowerCase(),
    })) || [];

  const columns = useMemo(
    () => [
      {
        header: 'Document Name',
        accessorKey: 'documentName',
          cell: ({ getValue }) => (
          <Typography sx={{ fontWeight: 600 }}>{getValue()}</Typography> // 
        ),
      },
      {
        header: 'Preview',
        accessorKey: 'url',
        cell: ({ row }) => {
          const { url, extension, documentName } = row.original;
          const fileType = extension === '.pdf' ? 'pdf' : 'image';

          return (
            <>
              {fileType === 'pdf' ? (
                <Button
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={pdfButtonSx}
                >
                  <PictureAsPdf
                    sx={{ fontSize: 40, mb: 0.5, color: 'error.main' }}
                  />
                  <Typography variant="caption" noWrap>
                    View PDF
                  </Typography>
                </Button>
              ) : (
                <PhotoProvider>
                  <PhotoView
                    src={
                      url ||
                      'https://www.thermaxglobal.com/wp-content/uploads/2020/05/image-not-found.jpg'
                    }
                  >
                    <Box
                      component="img"
                      src={
                        url ||
                        'https://www.thermaxglobal.com/wp-content/uploads/2020/05/image-not-found.jpg'
                      }
                      alt={documentName}
                      sx={{
                        width: 150,
                        height: 150,
                        objectFit: 'cover',
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    />
                  </PhotoView>
                </PhotoProvider>
              )}
            </>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });


  return (
    <PageContent>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Transaction Documents</h2>
      </Box>

      <TanstackReactTable
        table={table}
        isLoading={isLoading}
        data={data}
        columnsCount={columns.length}
      />
    </PageContent>
  );
};

export default TransactionDocuments;
